import styled from "styled-components";
import { Link } from "react-router-dom";
import { HiArrowRight } from "react-icons/hi2";
import optimizedQuestions from "../data/optimized-questions.json";
import OptimizedQuestion from "../ui/OptimizedQuestion";
import atchezaMarkerIcon from "../assets/images/atcheza-marker.png";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 2.5rem 0 2rem;

  @media only screen and (max-width: 840px) {
    padding: 2.5rem 1rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.3;
  margin: 0 0 0.5rem 0;
  color: var(--color-secondary);
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
  color: var(--color-text-secondary);

  text-align: justify;
  hyphens: auto;
`;

const DeploymentCard = styled.section`
  display: flex;
  gap: 0.875rem;
  align-items: flex-start;

  padding: 0.875rem 1rem;
  margin: 0.5rem 0;

  background: var(--deployment-card-bg);
  border-radius: 6px;
  border-left: 3px solid var(--color-secondary);
`;

const AtchezaMarker = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background-color: #0c798b;
  border-radius: 10px;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const AtchezaMarkerIcon = styled.img`
  width: 75%;
  height: auto;
  display: block;
`;

const DeploymentText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DeploymentHeadline = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
  color: var(--color-text-secondary);

  & strong {
    font-weight: 500;
  }
`;

const DeploymentDescription = styled.p`
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.55;
  margin: 0;
  color: var(--color-text-secondary);
  opacity: 0.85;
`;

const OptimizedQuestions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;

  color: var(--chat-color);

  @media only screen and (max-width: 425px) {
    flex-direction: column;
    align-items: center;
  }
`;

const SecondaryCTA = styled(Link)`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;

  color: var(--color-secondary);
  font-weight: 500;

  transition: opacity 0.2s;

  &:hover {
    opacity: 0.75;
  }

  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

function Landing() {
  return (
    <Container>
      <Title>Talk to Atcheza.</Title>

      <Paragraph>
        Benthos is data sovereignty infrastructure for coastal communities across the Global South. It runs on WhatsApp, deployed with local partner organizations rooted in each territory.
      </Paragraph>

      <Paragraph>
        The same conversational interface is available in this web app.
      </Paragraph>

      <DeploymentCard>
        <AtchezaMarker>
          <AtchezaMarkerIcon src={atchezaMarkerIcon} alt="" />
        </AtchezaMarker>
        <DeploymentText>
          <DeploymentHeadline>
            First active deployment: Atcheza, in Arraial do Cabo, Brazil.
          </DeploymentHeadline>
          <DeploymentDescription>
            Covers fisheries regulation, marine ecology and governance,<br />
            alongside the rights and culture of this coast.
          </DeploymentDescription>
        </DeploymentText>
      </DeploymentCard>

      <Paragraph>Sample questions:</Paragraph>

      {optimizedQuestions.map((oq, rowIdx) => (
        <OptimizedQuestions key={rowIdx}>
          {oq.map(({ label, question, type }, cardIdx) => (
            <OptimizedQuestion
              key={cardIdx}
              label={label}
              question={question}
              type={type}
            />
          ))}
        </OptimizedQuestions>
      ))}

      <SecondaryCTA to="/chat">
        Or ask your own question
        <HiArrowRight />
      </SecondaryCTA>
    </Container>
  );
}

export default Landing;
